import React, { useState, useRef, useEffect } from 'react';
import '../../styles/verification-code-input-mobile.css';

const VerificationCodeInput = ({
  length = 6,
  value = '',
  onChange,
  onComplete,
  disabled = false,
  error = false,
  autoFocus = true
}) => {
  const [codes, setCodes] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Update internal state when value prop changes
  useEffect(() => {
    if (value !== codes.join('')) {
      const newCodes = value.split('').slice(0, length);
      while (newCodes.length < length) {
        newCodes.push('');
      }
      setCodes(newCodes);
    }
  }, [value, length, codes]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, newValue) => {
    // Only allow single digits
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }

    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newCodes = [...codes];
    newCodes[index] = newValue;
    setCodes(newCodes);

    const fullCode = newCodes.join('');
    onChange?.(fullCode);

    // Auto-advance to next input
    if (newValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete when all fields are filled
    if (fullCode.length === length && onComplete) {
      onComplete(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!codes[index] && index > 0) {
        // If current field is empty, move to previous field and clear it
        const newCodes = [...codes];
        newCodes[index - 1] = '';
        setCodes(newCodes);
        onChange?.(newCodes.join(''));
        inputRefs.current[index - 1]?.focus();
      } else if (codes[index]) {
        // If current field has value, clear it
        const newCodes = [...codes];
        newCodes[index] = '';
        setCodes(newCodes);
        onChange?.(newCodes.join(''));
      }
    }
    // Handle arrow keys
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    // Handle Enter key
    else if (e.key === 'Enter') {
      const fullCode = codes.join('');
      if (fullCode.length === length && onComplete) {
        onComplete(fullCode);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');
    const pastedCodes = pastedData.replace(/\D/g, '').split('').slice(0, length);
    
    const newCodes = Array(length).fill('');
    pastedCodes.forEach((code, index) => {
      if (index < length) {
        newCodes[index] = code;
      }
    });
    
    setCodes(newCodes);
    const fullCode = newCodes.join('');
    onChange?.(fullCode);

    // Focus the next empty field or the last field
    const nextEmptyIndex = newCodes.findIndex(code => !code);
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : length - 1;
    inputRefs.current[focusIndex]?.focus();

    // Call onComplete if all fields are filled
    if (fullCode.length === length && onComplete) {
      onComplete(fullCode);
    }
  };

  const handleFocus = (index) => {
    // Select all text when focusing
    inputRefs.current[index]?.select();
  };

  return (
    <div className="verification-code-input flex gap-2 justify-center">
      {codes.map((code, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={code}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={`
            verification-code-digit
            w-12 h-12 text-center text-xl font-bold border-2 rounded-lg
            transition-all duration-200 focus:outline-none
            sm:w-12 sm:h-12 sm:text-xl
            md:w-14 md:h-14 md:text-2xl
            ${error 
              ? 'border-red-500 bg-red-50 text-red-900 focus:border-red-600 focus:ring-2 focus:ring-red-200' 
              : 'border-gray-300 bg-white text-gray-900 focus:border-purple-600 focus:ring-2 focus:ring-purple-200'
            }
            ${disabled 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'hover:border-gray-400'
            }
            ${code 
              ? (error ? 'bg-red-50' : 'bg-purple-50 border-purple-400') 
              : ''
            }
          `}
          style={{
            fontSize: '16px', // Prevents zoom on iOS
            WebkitAppearance: 'none', // Remove iOS styling
            MozAppearance: 'textfield' // Remove Firefox styling
          }}
          aria-label={`Verification code digit ${index + 1}`}
          aria-describedby={error ? 'verification-error' : undefined}
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;